'use client';

/**
 * The owner's INBOX for one agent (build plan M1-06), mounted inside a
 * `CollapsibleSection` on components/agent-details.tsx.
 *
 * TWO LISTS, ONE SURFACE, and the second is why the section is called Inbox
 * rather than Requests. The first is undecided FIRST CONTACTS: strangers held
 * at the door until the owner accepts, declines or blocks. The second is HELD
 * TURNS: conversations the agent has already been admitted to but is not
 * answering, because its owner set the cadence to "Only when I ask"
 * (lib/turns/release.ts). Both are things waiting on a person, which is what
 * an inbox is, and the second list is the release door that policy could not
 * be offered without.
 *
 * WHEN IT MOUNTS. The panel renders this component only once the person has
 * expanded the section (the `opened` function child of `CollapsibleSection`)
 * and only while the `agentInbox` flag from /api/messaging/feature-flags is
 * true. So the fetch in the mount effect below is the FIRST OPEN, not the
 * panel load: until 2026-09-07 a plain `<details>` mounted it collapsed and
 * every agent panel open paid one list request nobody had asked for. The
 * component itself still fetches on mount, unconditionally, because that is
 * the contract its tests render it against; the deferral lives in the
 * caller.
 *
 * WHAT A PERSON SEES. Who wrote (a sender label the server already capped
 * and header-sanitised), what about (a subject capped at 200 characters),
 * when, how many times, and how the message arrived. Three buttons since
 * M2-01 landed (2026-09-08): Accept, Decline and Block. There is still no
 * reply box and no preview, because the body is never fetched here; accepting
 * is what creates the conversation, and the conversation is where the body
 * goes.
 *
 * ACCEPT IS NOT A THIRD DECLINE. The other two end a ticket with one UPDATE.
 * Accept runs `acceptContact` (lib/inbox/accept.ts), which materialises a
 * chat, mints the sender's row if they have no account, and raises the turn
 * that makes the agent answer. It is the only button here that creates
 * anything, which is why it answers with a `chatId` and offers a link rather
 * than just dropping the row.
 *
 * STRANGER TEXT IS TEXT. Every field in a row was written by someone who
 * has no relationship with this owner yet. It is rendered as React text
 * nodes only: no Markdown, no `dangerouslySetInnerHTML`, no link detection,
 * no `<a>` built from a field, no image or favicon fetch keyed on a sender
 * domain, and nothing here is ever passed to a model. The body is not even
 * fetched: `listPendingContacts` selects no body column and this component
 * has no field for one.
 *
 * LIVE REFRESH. A new pending row makes the server create a `system`
 * notification with `data.kind = 'inbox_request'` (lib/inbox/notify.ts). The
 * `notification.new` frame it produces on the owner's UAMP socket is the
 * refetch trigger here, filtered on this agent's id; the bell row is the
 * durable fallback when the frame is missed.
 *
 * "Show filtered" lists the rows the deterministic screen marked as likely
 * bulk mail. They are never deleted by that verdict, so a false positive can
 * still be seen and accepted.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Ban, Check, Globe, Mail, MessageSquare, PauseCircle, Play, ShieldAlert, X } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { useUAMP } from '@/hooks/useUAMP';
import { Spinner } from '@/components/ui/spinner';
import Link from 'next/link';
import { formatRelativeTime } from '@/lib/utils';

/** Mirrors `PendingContactSummary` after JSON: dates are ISO strings. There is no body field. */
export interface AgentRequestRow {
  id: string;
  agentId: string;
  senderLabel: string | null;
  senderAuth: 'platform' | 'dmarc_aligned' | 'unauthenticated';
  transport: 'portal' | 'smtp';
  securityVerdict: 'received' | 'unauthenticated' | 'spam' | 'blocked';
  /** `filter:listing_request`: the mail became a find; shown behind "Show filtered". */
  relevanceReasonCode?: string | null;
  state: 'pending' | 'deferred' | string;
  pendingSubject: string | null;
  pendingReceivedAt: string | null;
  inboundCount: number;
  expiresAt: string;
}

/**
 * A turn the agent is HOLDING because its owner chose "Only when I ask"
 * (lib/turns/release.ts). Mirrors `HeldTurnSummary` after JSON.
 *
 * It carries no message body, for the same reason the ticket rows do not: this
 * is a settings panel, and the conversation is where the words live. What the
 * owner needs here is who is waiting, how many, and a button.
 */
export interface HeldTurnRow {
  turnId: string;
  chatId: string;
  transport: 'portal' | 'smtp';
  from: string | null;
  waitingCount: number;
  heldSince: string;
  expiresAt: string;
}

/** The `data.kind` lib/inbox/notify.ts writes; the refetch trigger matches on it. */
export const INBOX_REQUEST_NOTIFICATION_KIND = 'inbox_request';

/** `block` is owner-scoped: it closes every agent this person owns to the sender, not only this one. */
type Decision = 'accept' | 'decline' | 'block';

/**
 * The sender's platform handle, or null when there is not one.
 *
 * A portal request's label is `@handle`; an email request's is the address it
 * came from, which resolves to no account and must not become a link.
 */
function handleOf(senderLabel: string | null): string | null {
  if (!senderLabel) return null;
  const trimmed = senderLabel.trim();
  if (!trimmed.startsWith('@')) return null;
  const handle = trimmed.slice(1);
  return /^[a-z0-9._-]{1,64}$/i.test(handle) ? handle : null;
}

/** One line, collapsed whitespace, for a field a stranger typed. */
function oneLine(text: string | null | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

export function AgentRequests({ agentId }: { agentId: string }) {
  const t = useT();
  const [rows, setRows] = useState<AgentRequestRow[] | null>(null);
  const [held, setHeld] = useState<HeldTurnRow[]>([]);
  const [releasing, setReleasing] = useState<string | null>(null);
  const [releasedIds, setReleasedIds] = useState<string[]>([]);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [showFiltered, setShowFiltered] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  /**
   * Conversations this session accepted, so the row can be replaced by a link
   * to the chat it just created rather than vanishing. Keyed by request id and
   * deliberately not persisted: on the next load the ticket is `accepted` and
   * out of the pending list, and the conversation is in the person's chats
   * like any other.
   */
  const [acceptedChats, setAcceptedChats] = useState<Record<string, string>>({});
  const showFilteredRef = useRef(false);
  useEffect(() => { showFilteredRef.current = showFiltered; }, [showFiltered]);

  const load = useCallback(async (filtered: boolean) => {
    try {
      const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/requests${filtered ? '?filtered=1' : ''}`, { cache: 'no-store' });
      if (!res.ok) { setError(true); return; }
      const data = (await res.json()) as { requests?: AgentRequestRow[]; held?: HeldTurnRow[] };
      setRows(Array.isArray(data.requests) ? data.requests : []);
      setHeld(Array.isArray(data.held) ? data.held : []);
      setError(false);
    } catch {
      setError(true);
    }
  }, [agentId]);

  useEffect(() => { setRows(null); void load(showFiltered); }, [load, showFiltered]);

  const { onUAMPEvent } = useUAMP();
  useEffect(() => {
    const off = onUAMPEvent((event) => {
      const e = event as { type?: string; notification?: { data?: { kind?: string; agentId?: string } } };
      if (e.type !== 'notification.new') return;
      if (e.notification?.data?.kind !== INBOX_REQUEST_NOTIFICATION_KIND) return;
      if (e.notification.data.agentId !== agentId) return;
      void load(showFilteredRef.current);
    });
    return off;
  }, [agentId, onUAMPEvent, load]);

  /**
   * "Answer now": unpark one held turn. The row is not removed, it is marked
   * started, because the answer is not instant -- `releaseTurn` sets `due_at`
   * to now and the dispatcher claims it on its next pass. Dropping the row
   * would read as "done" while the agent has not spoken yet.
   */
  const release = async (row: HeldTurnRow) => {
    setReleasing(row.turnId);
    setReleaseError(null);
    try {
      const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/requests`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ turnId: row.turnId }),
      });
      if (!res.ok) {
        // 404 is every refusal this route makes, deliberately (it would
        // otherwise be an oracle for turn ids). The one a person can act on is
        // "somebody already started it", so that is what it says.
        setReleaseError(res.status === 404 ? t('agents.inboxHeldGone') : t('agents.inboxHeldReplyFailed'));
        return;
      }
      setReleasedIds((prev) => [...prev, row.turnId]);
    } catch {
      setReleaseError(t('agents.inboxHeldReplyFailed'));
    } finally {
      setReleasing(null);
    }
  };

  const decide = async (row: AgentRequestRow, decision: Decision) => {
    if (decision === 'block' && !window.confirm(t('agents.requestBlockConfirm'))) return;
    setBusyId(row.id);
    setDecisionError(null);
    try {
      const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/requests`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ contactId: row.id, decision }),
      });
      if (!res.ok) {
        // 410 is the one refusal worth its own sentence: the ticket's held
        // messages were already swept, so there is nothing left to open a
        // conversation with and retrying will never help.
        setDecisionError(
          res.status === 410
            ? t('agents.requestAcceptGone')
            : decision === 'accept'
              ? t('agents.requestAcceptFailed')
              : t('agents.requestDecisionFailed'),
        );
        return;
      }
      if (decision === 'accept') {
        const body = (await res.json().catch(() => null)) as { chatId?: string } | null;
        // What we link to is the SENDER'S HANDLE, not the chat's uuid, and the
        // difference is the whole defect this replaced. `/workspace?chat=<v>`
        // resolves `v` as a USERNAME; a uuid is deliberately a no-op there
        // (`lib/workspaces/url-actions.ts`, the `chat` deep link), so the
        // obvious `/chat/<chatId>` link silently landed on a bare workspace
        // and opened nothing. Found by a browser pass, 2026-09-08.
        //
        // An smtp sender has an address, not a handle, and nothing resolves
        // it: those rows keep no link rather than offer a dead one.
        const handle = handleOf(row.senderLabel);
        if (body?.chatId && handle) {
          setAcceptedChats((prev) => ({ ...prev, [row.id]: handle }));
          return;
        }
      }
      // Every decision leaves the pending set; drop the row rather than
      // refetch so the list does not jump while the person is reading it.
      setRows((prev) => (prev ? prev.filter((r) => r.id !== row.id) : prev));
    } catch {
      setDecisionError(t('agents.requestDecisionFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const timeWords = { justNow: t('time.justNow'), minutesAgo: t('time.minutesAgo'), hoursAgo: t('time.hoursAgo'), daysAgo: t('time.daysAgo') };

  return (
    <div className="space-y-4">
      {/* THE HELD LIST COMES FIRST, and only exists while something is held.
          It is the release door `lib/turns/subscription.ts` requires before
          `manual` may be offered at all, so it is not decoration: without a
          rendered "Answer now" the policy parks conversations that nothing can
          restart. An owner on any other cadence never sees this block. */}
      {held.length > 0 && (
        <div className="rounded-lg border border-border p-3 space-y-3">
          <div>
            <h4 className="text-sm font-medium flex items-center gap-2">
              <PauseCircle className="h-4 w-4 text-muted-foreground shrink-0" />
              {t('agents.inboxHeldTitle')}
            </h4>
            <p className="text-xs text-muted-foreground mt-1">{t('agents.inboxHeldDesc')}</p>
          </div>
          <ul className="space-y-2">
            {held.map((h) => {
              const started = releasedIds.includes(h.turnId);
              return (
                <li key={h.turnId} className="flex flex-col gap-2 @lg/main:flex-row @lg/main:items-center @lg/main:justify-between border-t border-border pt-2 first:border-t-0 first:pt-0">
                  <div className="min-w-0 flex-1">
                    {/* `min-w-0` for the same reason as the ticket row below:
                        without it this wrapper sizes to max-content and the
                        label bleeds past the card. A held turn's `from` is a
                        handle rather than an address, so it is shorter, but the
                        failure mode is identical and so is the fix. */}
                    <div className="min-w-0 flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium break-words">{h.from ?? t('agents.requestUnknownSender')}</span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                        {h.transport === 'smtp' ? <Mail className="h-3 w-3" /> : <Globe className="h-3 w-3" />}
                        {h.transport === 'smtp' ? t('agents.requestViaEmail') : t('agents.requestViaPlatform')}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {t('agents.inboxHeldMessages', { count: h.waitingCount })}
                      {' · '}
                      {formatRelativeTime(h.heldSince, timeWords)}
                    </p>
                  </div>
                  {started ? (
                    <span className="text-xs text-muted-foreground shrink-0">{t('agents.inboxHeldStarted')}</span>
                  ) : (
                    <button
                      type="button"
                      className="btn-primary text-xs flex items-center gap-1 shrink-0 self-start"
                      disabled={releasing === h.turnId}
                      onClick={() => void release(h)}
                    >
                      {releasing === h.turnId ? <Spinner size="sm" /> : <Play className="h-3.5 w-3.5" />}
                      {t('agents.inboxHeldReply')}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          {releaseError && <p className="text-xs text-destructive">{releaseError}</p>}
        </div>
      )}

      <p className="text-sm text-muted-foreground">{t('agents.requestsDesc')}</p>

      {error ? (
        <p className="text-sm text-destructive">{t('agents.requestsLoadFailed')}</p>
      ) : rows === null ? (
        <div className="flex justify-center py-4"><Spinner size="md" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 @md/main:p-8 text-center">
          <Mail className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t('agents.requestsEmpty')}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => {
            const sender = oneLine(row.senderLabel) || t('agents.requestUnknownSender');
            const subject = oneLine(row.pendingSubject) || t('agents.requestNoSubject');
            const busy = busyId === row.id;
            return (
              /* STACKED UNTIL THERE IS ROOM, and the row was unreadable without
                 it. The actions are `shrink-0` and measure 266px with their
                 labels; `min-w-0 flex-1` lets the text column shrink to
                 nothing, so in the 348px row of a default-width agent panel
                 the sender's name got 44px and wrapped ONE CHARACTER PER LINE
                 (measured on dev 2026-09-09). Horizontal only from `@lg`
                 (512px), which is the first width where 266px of buttons still
                 leaves the name and subject a readable column. */
              <li key={row.id} className="border border-border rounded-lg p-3 flex flex-col gap-3 @lg/main:flex-row @lg/main:items-start @lg/main:justify-between">
                <div className="min-w-0 flex-1">
                  {/* `min-w-0` HERE TOO, not only on the column. A flex item's
                      default `min-width: auto` sizes it to max-content, so this
                      wrapper grew to fit the sender label and `break-words` on
                      the span below never engaged: a 63-character email address
                      measured 354px inside a 322px column and bled 21px past
                      the card's right border. The outer `min-w-0` cannot help,
                      because the overflow is one level further in. An email
                      address is the normal case for this list, not an edge one.
                      Caught by a browser pass, 2026-09-09. */}
                  <div className="min-w-0 flex items-center gap-2 flex-wrap">
                    {/* The label is a text node. It is never a link, never an avatar lookup. */}
                    {/* `break-words`, not `break-all`: an email address has to
                        be breakable, but `break-all` also splits ordinary names
                        mid-word when the column is merely snug. */}
                    <span className="text-sm font-medium break-words">{sender}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                      {row.transport === 'smtp' ? <Mail className="h-3 w-3" /> : <Globe className="h-3 w-3" />}
                      {row.transport === 'smtp' ? t('agents.requestViaEmail') : t('agents.requestViaPlatform')}
                    </span>
                    {row.senderAuth === 'unauthenticated' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-600 dark:text-amber-400">
                        <ShieldAlert className="h-3 w-3" />
                        {t('agents.requestUnverified')}
                      </span>
                    )}
                    {(row.securityVerdict === 'spam' || row.relevanceReasonCode === 'filter:listing_request') && (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{t('agents.requestFiltered')}</span>
                    )}
                    {row.state === 'deferred' && (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{t('agents.requestPostponed')}</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 break-words">{subject}</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    {row.pendingReceivedAt ? formatRelativeTime(row.pendingReceivedAt, timeWords) : ''}
                    {row.inboundCount > 1 ? ` · ${t('agents.requestMessagesCount', { count: row.inboundCount })}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {acceptedChats[row.id] ? (
                    <Link href={`/workspace?chat=${encodeURIComponent(acceptedChats[row.id])}`} className="btn-secondary text-xs flex items-center gap-1">
                      <MessageSquare className="h-3.5 w-3.5" />
                      {t('agents.requestOpenChat')}
                    </Link>
                  ) : (
                  <>
                  <button type="button" className="btn-primary text-xs flex items-center gap-1" disabled={busy} onClick={() => void decide(row, 'accept')}>
                    {busy ? <Spinner size="sm" /> : <Check className="h-3.5 w-3.5" />}
                    {t('agents.requestAccept')}
                  </button>
                  <button type="button" className="btn-secondary text-xs flex items-center gap-1" disabled={busy} onClick={() => void decide(row, 'decline')}>
                    {busy ? <Spinner size="sm" /> : <X className="h-3.5 w-3.5" />}
                    {t('agents.requestDecline')}
                  </button>
                  <button type="button" className="btn-secondary text-xs flex items-center gap-1" disabled={busy} onClick={() => void decide(row, 'block')}>
                    <Ban className="h-3.5 w-3.5" />
                    {t('agents.requestBlock')}
                  </button>
                  </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {decisionError && <p className="text-xs text-destructive">{decisionError}</p>}

      <button type="button" className="btn-ghost text-xs" onClick={() => setShowFiltered((v) => !v)}>
        {showFiltered ? t('agents.requestHideFiltered') : t('agents.requestShowFiltered')}
      </button>
    </div>
  );
}
